import { useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { notificationQueryKeys } from './queryKeys';

/**
 * Keeps the bell live without a second socket connection.
 *
 * `features/realtime/useRealtime.ts` — another agent's file, mounted once in
 * AppShell — already turns every socket event into a targeted
 * `invalidateQueries` call (`['ws', ws, 'requests']`, `['ws', ws, 'requests',
 * id, 'messages']`, `['ws', ws, 'board']`, …). It has no idea this feature
 * exists, so none of those calls ever name `['ws', ws, 'notifications']`
 * directly, and this component would otherwise sit stale until something else
 * happened to refetch it.
 *
 * Rather than open a second `socket.io-client` connection here — which would
 * duplicate useRealtime's auth handshake, reconnect handling and echo
 * suppression for no benefit — this subscribes to the QUERY CACHE instead.
 * Every one of useRealtime's invalidations is an observable event on the
 * cache; this watches for any of them landing under this workspace's prefix
 * and, when one does, invalidates the notifications keys too. Still
 * invalidate-don't-push: nothing here reads a socket payload, it only reacts
 * to TanStack Query's own "this is stale" signal — the same convention
 * useRealtime.ts itself follows, just one layer removed from the socket.
 *
 * The self-exclusion (`isOwnKey`) matters: without it, invalidating the
 * notifications keys would itself fire this subscription again. TanStack does
 * not literally recurse forever — invalidating an already-invalidated query is
 * a no-op — but skipping it is one line and removes the question.
 */
export function useNotificationsRealtimeRefresh(workspaceId: string): void {
  const queryClient = useQueryClient();

  useEffect(() => {
    const workspacePrefix: readonly unknown[] = ['ws', workspaceId];
    const notificationsPrefix = notificationQueryKeys.all(workspaceId);

    const startsWith = (key: readonly unknown[], prefix: readonly unknown[]): boolean =>
      prefix.every((part, index) => key[index] === part);

    const unsubscribe = queryClient.getQueryCache().subscribe((event) => {
      if (event.type !== 'updated' || event.action.type !== 'invalidate') return;

      const key = event.query.queryKey;
      if (!startsWith(key, workspacePrefix)) return;
      if (startsWith(key, notificationsPrefix)) return;

      void queryClient.invalidateQueries({ queryKey: notificationsPrefix });
    });

    return unsubscribe;
  }, [queryClient, workspaceId]);
}
